/**
 * RouteErrorBoundary — Route-scoped error boundary that resets on navigation.
 * Prevents a crash in one page from showing the global crash screen.
 * Automatically clears error state when the user navigates to another route.
 */
import { Component, type ReactNode } from "react";
import { useLocation, useNavigate } from "react-router-dom";

interface InnerProps {
  children: ReactNode;
  locationKey: string;
  onGoHome: () => void;
  onReload: () => void;
}

interface InnerState {
  hasError: boolean;
}

class RouteErrorBoundaryInner extends Component<InnerProps, InnerState> {
  constructor(props: InnerProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): InnerState {
    return { hasError: true };
  }

  componentDidUpdate(prevProps: InnerProps) {
    // Auto-reset when location changes (user navigated away)
    if (this.state.hasError && prevProps.locationKey !== this.props.locationKey) {
      this.setState({ hasError: false });
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center justify-center gap-4 px-4 py-16 text-center">
          <div className="rounded-xl border border-border bg-card p-8 max-w-sm w-full space-y-4">
            <p className="text-4xl">😵‍💫</p>
            <h2 className="text-lg font-semibold text-foreground">
              Cette page a rencontré un souci
            </h2>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Pas de panique — le reste de l'app fonctionne. Tu peux recharger cette page ou revenir au tableau de bord.
            </p>
            <div className="flex flex-col gap-2">
              <button
                onClick={this.props.onReload}
                className="w-full rounded-lg bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
              >
                Recharger la page
              </button>
              <button
                onClick={this.props.onGoHome}
                className="w-full rounded-lg border border-border bg-background px-4 py-2.5 text-sm font-medium text-foreground hover:bg-muted transition-colors"
              >
                Retour au tableau de bord
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default function RouteErrorBoundary({ children }: { children: ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();

  return (
    <RouteErrorBoundaryInner
      locationKey={location.key}
      onGoHome={() => navigate("/dashboard")}
      onReload={() => window.location.reload()}
    >
      {children}
    </RouteErrorBoundaryInner>
  );
}
